import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import toast from 'react-hot-toast';
import { Download, Mail, Check, AlertCircle } from 'lucide-react';

export default function CustomersPage() {
  const { t } = useTranslation();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [avvByPractice, setAvvByPractice] = useState({});

  useEffect(() => {
    Promise.all([
      api.get('/api/practices'),
      api.get('/api/avv/platform/status').catch(() => ({ data: { practices: [] } })),
    ]).then(([r1, r2]) => {
      setList(r1.data.practices || []);
      const map = {};
      for (const p of r2.data.practices || []) map[p._id] = p;
      setAvvByPractice(map);
    }).finally(() => setLoading(false));
  }, []);

  function downloadAvv(practiceId, slug, lang = 'de') {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/avv/platform/${practiceId}/download?lang=${lang}`;
    const headers = { Authorization: `Bearer ${localStorage.getItem('studioops_access_token')}` };
    const pid = localStorage.getItem('studioops_active_practice');
    if (pid) headers['X-Practice-Id'] = pid;
    fetch(url, { headers })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement('a');
        const objUrl = URL.createObjectURL(blob);
        a.href = objUrl;
        a.download = `${lang === 'en' ? 'DPA' : 'AVV'}_${slug || practiceId}.pdf`;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => { URL.revokeObjectURL(objUrl); a.remove(); }, 500);
      })
      .catch(() => toast.error(t('settings.publicPage.downloadFailed')));
  }

  async function requestResign(practiceId, practiceName) {
    try {
      await api.post(`/api/avv/platform/${practiceId}/resign-request`, {});
      toast.success(t('customers.resignSent', { name: practiceName }));
    } catch (e) {
      toast.error(e.response?.data?.error || t('customers.resignFailed'));
    }
  }

  useEffect(() => {
    api.get('/api/practices')
      .then((r) => setList(r.data.practices || []))
      .finally(() => setLoading(false));
  }, []);

  const mrr = list
    .filter((p) => ['active', 'trialing', 'past_due'].includes(p.subscription_status))
    .reduce((s, p) => s + (Number(p.monthly_amount_eur) || 0), 0);

  return (
    <div data-testid="customers-page">
      <h1 className="text-2xl font-bold tracking-tight">{t('customers.title')}</h1>
      <p className="mt-1 text-sm text-text-secondary">{t('customers.subtitle')}</p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
        <KpiCard label={t('customers.kpi.active')} value={list.filter((p) => p.subscription_status === 'active').length} testid="kpi-active"/>
        <KpiCard label={t('customers.kpi.trialing')} value={list.filter((p) => p.subscription_status === 'trialing').length} testid="kpi-trialing"/>
        <KpiCard label={t('customers.kpi.mrr')} value={`${mrr.toLocaleString(t('locale.numberFormat') || 'de-AT')} €`} testid="kpi-mrr"/>
        <KpiCard label={t('customers.kpi.total')} value={list.length} testid="kpi-total"/>
      </div>

      <div className="so-card mt-6 overflow-x-auto">
        {loading ? (
          <div className="p-10 text-center text-text-secondary text-sm">{t('common.loading')}</div>
        ) : list.length === 0 ? (
          <div className="p-10 text-center text-text-secondary">{t('common.noData')}</div>
        ) : (
          <table className="so-table">
            <thead>
              <tr>
                <th>{t('customers.table.name')}</th>
                <th>{t('customers.table.slug')}</th>
                <th>{t('customers.table.plan')}</th>
                <th>{t('customers.table.billing')}</th>
                <th className="text-right">{t('customers.table.mrrCol')}</th>
                <th>{t('customers.table.commitmentUntil')}</th>
                <th>{t('customers.table.status')}</th>
                <th>{t('customers.table.avv')}</th>
                <th>{t('customers.table.subaccount')}</th>
                <th>{t('customers.table.number')}</th>
                <th>{t('customers.table.lang')}</th>
                <th>{t('customers.table.publicEnabled')}</th>
              </tr>
            </thead>
            <tbody>
              {list.map((p) => (
                <tr key={p._id} data-testid={`practice-row-${p._id}`}>
                  <td className="font-medium">{p.name}</td>
                  <td className="font-mono text-xs">{p.slug || '—'}</td>
                  <td>
                    {p.plan ? (
                      <span className={`so-pill ${p.plan === 'pro' ? 'so-pill-accent' : 'so-pill-neutral'}`} data-testid={`practice-plan-${p._id}`}>
                        {p.plan === 'pro' ? 'Pro' : 'Starter'}
                      </span>
                    ) : <span className="text-text-secondary">—</span>}
                  </td>
                  <td>
                    <span className={`so-pill ${p.billing_cycle === 'annual' ? 'so-pill-success' : 'so-pill-neutral'}`} data-testid={`practice-cycle-${p._id}`}>
                      {p.billing_cycle === 'annual' ? t('revenue.cycle.annualShort') : t('revenue.cycle.monthlyShort')}
                    </span>
                  </td>
                  <td className="text-right font-mono" data-testid={`practice-mrr-${p._id}`}>{p.monthly_amount_eur ? `${p.monthly_amount_eur} €` : '—'}</td>
                  <td className="text-text-secondary text-xs" data-testid={`practice-contract-${p._id}`}>{p.contract_end ? new Date(p.contract_end).toLocaleDateString(t('locale.numberFormat') || 'de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—'}</td>
                  <td><span className={`so-pill ${p.subscription_status === 'active' ? 'so-pill-success' : p.subscription_status === 'trialing' ? 'so-pill-accent' : p.subscription_status === 'past_due' ? 'so-pill-warning' : 'so-pill-neutral'}`}>{statusLabel(p.subscription_status, t)}</span></td>
                  <td data-testid={`practice-avv-${p._id}`}>
                    {avvByPractice[p._id]?.signed ? (
                      <div className="flex items-center gap-1">
                        <span className="so-pill so-pill-success flex items-center gap-1"><Check size={10}/>{t('customers.avv.signed')}</span>
                        <button onClick={() => downloadAvv(p._id, p.slug, 'de')} className="text-text-secondary hover:text-accent" title={t('customers.avv.downloadDe')} data-testid={`avv-dl-${p._id}`}>
                          <Download size={13}/>
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1">
                        <span className="so-pill so-pill-warning flex items-center gap-1"><AlertCircle size={10}/>{t('customers.avv.pending')}</span>
                        <button onClick={() => requestResign(p._id, p.name)} className="text-text-secondary hover:text-accent" title={t('customers.avv.resignReminder')} data-testid={`avv-resign-${p._id}`}>
                          <Mail size={13}/>
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="font-mono text-[11px]">{p.twilio_subaccount_sid ? p.twilio_subaccount_sid.slice(0, 12) + '…' : '—'}</td>
                  <td className="font-mono text-xs">{p.twilio_whatsapp_number || '—'}</td>
                  <td className="font-mono text-xs uppercase">{p.language || 'de'}</td>
                  <td>{p.public_enabled ? <span className="so-pill so-pill-success">✓</span> : <span className="so-pill so-pill-neutral">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function KpiCard({ label, value, testid }) {
  return (
    <div className="so-card p-4" data-testid={testid}>
      <p className="text-[11px] uppercase tracking-wider text-text-secondary">{label}</p>
      <p className="text-2xl font-bold text-navy mt-1">{value}</p>
    </div>
  );
}

function statusLabel(s, t) {
  const key = `customers.status.${s}`;
  const translated = t ? t(key) : null;
  if (translated && translated !== key) return translated;
  return s || '—';
}
